import React, { memo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { TimeRangeSelector, Loading, NoData } from '@components';
import StockChart from './stockChart';
import D_Number from '@utils/number';
import D_Currency from '@utils/currency';

const INFOKEYS = {
  marketcap: 'marketcap',
  week52High: 'week52High',
  week52Low: 'week52Low',
};

const novalueContent = '-';

const formatCurrencyToString = (value, currencyIsoCode) => {
  if (!value || !currencyIsoCode) {
    return '-';
  }

  return D_Currency.format({
    value,
    currencyIsoCode,
  });
};

const RiseText = ({ value, percent }) => (
  <>
    <span> +{value || novalueContent}</span>
    <span className="green"> ({percent}%)</span>
    <span className="appkiticon icon-arrow-up-fill a-font-12 green" />
  </>
);

const FallText = ({ value, percent }) => (
  <>
    <span> -{value || novalueContent}</span>
    <span className="red"> ({percent || novalueContent}%)</span>
    <span className="appkiticon icon-arrow-down-fill a-font-12 red" />
  </>
);

const renderViariety = (data, currencyIsoCode) => {
  if (!Array.isArray(data) || Array.length === 0) {
    return null;
  }
  const [value, percent] = data;

  if (!value && !percent) {
    return null;
  }

  const showValue = formatCurrencyToString(Math.abs(value), currencyIsoCode);

  const Comp = percent > 0 ? RiseText : FallText;
  return (
    <Comp value={showValue} percent={D_Number.format(Math.abs(percent))} />
  );
};

const Ticker = memo(({ data = {}, selected, varietyModel }) => {
  const [stockViariety, setStockViariety] = useState([]),
    { currencyIsoCode = 'USD' } = data;

  useEffect(() => {
    if (Object.keys(data)[0]) {
      const viariety = varietyModel.map((key) => [
        data[key],
        data[`${key}Percent`],
      ]);

      setStockViariety(viariety);
    }
  }, [data, varietyModel]);

  return (
    <div className="ticker-wrapper">
      <div className="ticker">
        <span>{data.ticker || `Ticker:${novalueContent}`}</span>
      </div>
      <div className="numb">
        <span className="base">
          {formatCurrencyToString(
            data.latestClosingPrice || data.latestMarketCap,
            currencyIsoCode,
          )}
        </span>
        {renderViariety(stockViariety[selected], currencyIsoCode)}
      </div>
    </div>
  );
});

const StockInfo = memo(({ data }) => {
  const keys = Object.keys(data),
    { currencyIsoCode = 'USD' } = data;
  return (
    <div className="info-card">
      {keys.includes(INFOKEYS.week52High) && (
        <div className="info-text">
          52-wk high: {`  `}
          {formatCurrencyToString(data.week52High, currencyIsoCode)}
        </div>
      )}

      {keys.includes(INFOKEYS.week52Low) && (
        <div className="info-text">
          52-wk low: {`  `}
          {formatCurrencyToString(data.week52Low, currencyIsoCode)}
        </div>
      )}

      {keys.includes(INFOKEYS.marketcap) && (
        <div className="info-text">
          Mkt cap: {D_Number.format(Number(data.marketcap) || null)}
        </div>
      )}
    </div>
  );
});

const StockChartItem = (props) => {
  const chartRef = useRef(null);

  const {
    data = {},
    expand,
    selected,
    isLoading,
    timeRangeBtns,
    varietyModel,
  } = props;
  const { onRangeSelected } = props;

  const chartData = data.closingPriceList || data.marketCapList;

  const { companyName, currencyIsoCode = 'USD' } = data;

  const wrapperClassname = `stock-chart-wrapper ${
    expand && 'full-screen-chart'
  }`;

  if (isLoading) {
    return (
      <div className={wrapperClassname}>
        <Loading />
      </div>
    );
  }

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return (
      <div className={wrapperClassname}>
        <NoData />
      </div>
    );
  }

  return (
    <div className={wrapperClassname}>
      <Ticker data={data} selected={selected} varietyModel={varietyModel} />
      <TimeRangeSelector
        btns={timeRangeBtns}
        onClick={onRangeSelected}
        selected={selected}
      />
      <StockChart
        name={companyName}
        timeRangeBtns={timeRangeBtns}
        currencyIsoCode={currencyIsoCode}
        ref={chartRef}
        data={chartData}
        selector={selected}
      />
      <StockInfo data={data} />
    </div>
  );
};

StockChartItem.defaultProps = {
  expand: false,
};

StockChartItem.propTypes = {
  data: PropTypes.object.isRequired,
  expand: PropTypes.bool,
  selected: PropTypes.number.isRequired,
  onRangeSelected: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  varietyModel: PropTypes.array.isRequired,
};

export default memo(StockChartItem);
